import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import type { SortDirection } from '@/types/api'

export interface ListState {
  page: number
  perPage: number
  sort: string | undefined
  direction: SortDirection
  search: string
  filters: Record<string, string>
}

/**
 * List state (page, sort, search, filters) persisted in the URL so views are shareable and the
 * back button works. Changing any filter resets to page 1.
 */
export function useListParams(
  filterKeys: readonly string[] = [],
  defaults: { sort?: string; perPage?: number } = {},
) {
  const [params, setParams] = useSearchParams()

  const state: ListState = useMemo(() => {
    const filters: Record<string, string> = {}
    for (const key of filterKeys) {
      const v = params.get(key)
      if (v) filters[key] = v
    }
    return {
      page: Math.max(1, Number(params.get('page') ?? 1) || 1),
      perPage: Number(params.get('perPage') ?? defaults.perPage ?? 20) || 20,
      sort: params.get('sort') ?? defaults.sort,
      direction: params.get('direction') === 'desc' ? 'desc' : 'asc',
      search: params.get('search') ?? '',
      filters,
    }
  }, [params, filterKeys, defaults.sort, defaults.perPage])

  const update = useCallback(
    (patch: Record<string, string | number | undefined | null>, resetPage = true) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          for (const [k, v] of Object.entries(patch)) {
            if (v === undefined || v === null || v === '') next.delete(k)
            else next.set(k, String(v))
          }
          if (resetPage && !('page' in patch)) next.delete('page')
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  const toggleSort = useCallback(
    (key: string) => {
      const direction = state.sort === key && state.direction === 'asc' ? 'desc' : 'asc'
      update({ sort: key, direction })
    },
    [state.sort, state.direction, update],
  )

  const query = useMemo(
    () => ({
      page: state.page,
      perPage: state.perPage,
      sort: state.sort,
      direction: state.direction,
      search: state.search || undefined,
      ...state.filters,
    }),
    [state],
  )

  return { state, query, update, toggleSort, setPage: (page: number) => update({ page }, false) }
}
